<template>
  <div ref="containerRef" class="relative">
    <button
      type="button"
      class="inline-grid grid-flow-col items-center justify-center gap-1 rounded-lg bg-white px-2 py-1.5 text-xs font-medium text-gray-950 ring-1 ring-gray-950/10 transition duration-75 hover:bg-gray-50 ds:gap-1.5 ds:rounded-(--ds-radius-pill) ds:bg-(--ds-surface-default) ds:px-3.5 ds:py-2 ds:text-(length:--ds-font-size-sm) ds:font-semibold ds:text-(--ds-text-primary) ds:ring-(--ds-border-app) ds:hover:bg-(--ds-color-grey-50)"
      @click="open = !open"
    >
      <span class="w-1.5 h-1.5 rounded-full" :class="dotClass" />
      <span>{{ label }}</span>
      <svg
        class="w-3 h-3 opacity-60"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
          clip-rule="evenodd"
        />
      </svg>
    </button>

    <Floating :open="open" :anchor="containerRef" placement="bottom-end">
      <div
        ref="popupRef"
        class="max-h-[calc(100dvh-1rem)] w-72 max-w-[calc(100vw-1rem)] overflow-y-auto overscroll-contain bg-white rounded-xl shadow-lg ring-1 ring-gray-950/5 p-4"
      >
        <template v-if="!url">
          <div class="text-center text-xs text-gray-500 py-4">
            {{ description || $t('portal.editor.preview.not_available') }}
          </div>
          <a
            v-if="linkUrl"
            :href="linkUrl"
            class="flex items-center justify-center gap-1.5 w-full text-xs font-medium text-primary-600 hover:text-primary-700 py-1.5"
          >
            <svg
              class="w-3.5 h-3.5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                clip-rule="evenodd"
              />
            </svg>
            {{ linkLabel || $t('portal.editor.preview.go_to_pages') }}
          </a>
        </template>

        <template v-else>
          <p class="text-sm font-medium text-gray-900 text-center mb-3">
            {{ description }}
          </p>

          <div class="flex justify-center mb-3">
            <div
              class="inline-block p-2 bg-white rounded-lg ring-1 ring-gray-100"
            >
              <SuspenseWrapper without-loading>
                <QrCode :value="url" :size="200" />
              </SuspenseWrapper>
            </div>
          </div>

          <div class="flex items-center gap-1.5 mb-2">
            <input
              type="text"
              :value="url"
              readonly
              class="min-w-0 flex-1 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 truncate"
              @click="$event.target.select()"
            />
            <button
              type="button"
              class="shrink-0 inline-flex items-center px-2 py-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
              :title="
                copied
                  ? $t('portal.editor.preview.copied')
                  : $t('portal.editor.preview.copy_link')
              "
              @click="copyUrl"
            >
              <svg
                v-if="!copied"
                class="w-3.5 h-3.5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M13.887 3.182c.396.037.79.08 1.183.128C16.194 3.45 17 4.414 17 5.517V16.75A2.25 2.25 0 0114.75 19h-9.5A2.25 2.25 0 013 16.75V5.517c0-1.103.806-2.068 1.93-2.207.393-.048.787-.09 1.183-.128A3.001 3.001 0 019 1h2c1.373 0 2.531.923 2.887 2.182zM7.5 4A1.5 1.5 0 019 2.5h2A1.5 1.5 0 0112.5 4v.5h-5V4z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-else
                class="w-3.5 h-3.5 text-green-600"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>

          <a
            :href="url"
            target="_blank"
            rel="noopener"
            class="flex items-center justify-center gap-1.5 w-full text-xs font-medium text-primary-600 hover:text-primary-700 py-1.5"
          >
            <svg
              class="w-3.5 h-3.5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5zm7.25-.75a.75.75 0 01.75-.75h3.5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0V6.31l-5.47 5.47a.75.75 0 01-1.06-1.06l5.47-5.47H12.25a.75.75 0 01-.75-.75z"
                clip-rule="evenodd"
              />
            </svg>
            {{ $t('portal.editor.preview.open_new_tab') }}
          </a>

          <p v-if="note" class="text-tiny text-fg-mauve text-center mt-2">
            {{ note }}
          </p>
        </template>
      </div>
    </Floating>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import QrCode from '@/Components/QrCode.vue'
import SuspenseWrapper from '@/Components/common/SuspenseWrapper.vue'
import Floating from '@/Components/Seating/Floating.vue'

const props = defineProps({
  label: { type: String, required: true },
  url: { type: String, default: null },
  description: { type: String, default: null },
  dotColor: { type: String, default: 'gray' },
  note: { type: String, default: null },
  linkUrl: { type: String, default: null },
  linkLabel: { type: String, default: null },
})

const open = ref(false)
const copied = ref(false)
const containerRef = ref(null)
const popupRef = ref(null)

const dotClass = computed(
  () =>
    ({
      green: 'bg-green-500',
      amber: 'bg-amber-500',
      gray: 'bg-gray-300',
    })[props.dotColor] || 'bg-gray-300',
)

async function copyUrl() {
  copied.value = false

  try {
    await navigator.clipboard.writeText(props.url)
  } catch {
    return
  }

  copied.value = true
  setTimeout(() => {
    copied.value = false
  }, 2000)
}

function handleClickOutside(e) {
  if (
    containerRef.value &&
    !containerRef.value.contains(e.target) &&
    !popupRef.value?.contains(e.target)
  ) {
    open.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))
</script>
